import React, {useState, useEffect} from "react";
import {addressAndRatingDetails, deleteAddressAndRating, editAddressAndRating, flagAddressAndRating, getUserList} from "../../services/adminServices";
import moment from "moment";
import CustomLoader from "../../CustomComponents/CustomLoader";
import {Autocomplete, TextField, CircularProgress} from "@mui/material";
import {sweetAlertQuestion, sweetAlerts} from "../../utils/sweet-alerts";
import {useLocation} from "react-router-dom";
import CustomUserName from "../../CustomComponents/CustomUserName";
import CustomRatingStar from "../../CustomComponents/CustomRatingStar";
import {fDate} from "../../utils/format-time";
import {getStatusBadge} from "../../utils/format-text";

const MyRatings = () => {
	const location = useLocation();

	const [firstTimeLoad, setFirstTimeLoad] = useState(true);
	const [ratings, setRatings] = useState([]);
	const [filteredRatings, setFilteredRatings] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [filterStatus, setFilterStatus] = useState("all");
	const [filterRating, setFilterRating] = useState("all");
	const [sortBy, setSortBy] = useState("date-desc");
	const [currentPage, setCurrentPage] = useState(1);
	const [showDetailModal, setShowDetailModal] = useState(false);
	const [selectedRating, setSelectedRating] = useState(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [editingRating, setEditingRating] = useState(null);
	const [editFormData, setEditFormData] = useState({});
	const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
	const [editErrors, setEditErrors] = useState({});
	const [mockRatings, setMockRatings] = useState([]);
	const [loadingButtons, setLoadingButtons] = useState({}); // Track loading state for individual buttons
	const [page, setPage] = useState(1); // Track loading state for individual buttons
	const [pageSize, setPageSize] = useState(10); // Track loading state for individual buttons
	const [totalPages, setTotalPages] = useState(1);
	const [totalLength, setTotalLength] = useState(0);
	const [totalCount, setTotalCount] = useState(0);

	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState([]);
	const [loadingUser, setLoadingUser] = useState(true);
	const [debounceTimer, setDebounceTimer] = useState(null);
	const [selectedUserId, setSelectedUserId] = useState(null);

	const [inputValue, setInputValue] = useState("");

	// Fetch ratings data
	const getMockRatings = async () => {
		const dummyRatings = [];
		try {
			if (firstTimeLoad) {
				setLoading(false);
			}
			const response = await addressAndRatingDetails({
				pagesize: pageSize,
				page: page,
				rating: filterRating,
				status: filterStatus,
				sortBy: sortBy,
				userId: selectedUserId,
			});

			if (response && response.status === true && Array.isArray(response?.data?.list) && response?.data?.list?.length > 0) {
				const transformed = response?.data?.list?.map((item) => {
					const jobDate = item.job_date ? moment(item.job_date) : null;
					const submittedDate = item.createdAt ? moment(item.createdAt) : null;
					const likes = item?.likes || null;
					const trueCount = likes.filter((item) => item?.like == true).length;

					return {
						id: item.rating_id,
						jobAddress: item.address?.full_address || "N/A",
						jobRating: item.stars,
						jobDate: jobDate ? jobDate.format("YYYY-MM-DD") : "",
						jobIssues: typeof item?.job_issues === "string" ? JSON.parse(item?.job_issues) : item?.job_issues || [],
						status: item.is_historic ? "historic" : "fresh",
						submittedDate: submittedDate ? submittedDate.format("YYYY-MM-DD") : "",
						canEdit: !item.is_historic,
						daysAgo: jobDate ? moment().diff(jobDate, "days") : 0,
						flagged: item.admin_flagged || false,
						likeCount: trueCount,
						selfLike: item?.selfLike,
						userName: item?.user?.first_name + " " + item?.user?.last_name,
						item,
					};
				});

				setTotalCount(response?.data?.totalCount);
				setMockRatings(transformed);
				setTotalLength(response?.data?.totalCount);
				const totalPages = Math.ceil(response?.data?.totalCount / pageSize);
				setTotalPages(totalPages);

				setLoading(true);
			} else {
				setLoading(true);
				setMockRatings(dummyRatings);
			}
		} catch (error) {
			console.error("Error fetching ratings:", error);

			setMockRatings(dummyRatings);
		}
	};

	useEffect(() => {
		getMockRatings();
	}, [pageSize, page]);

	useEffect(() => {
		if (firstTimeLoad) {
			setFirstTimeLoad(false);
		} else {
			setPage(1);
			getMockRatings();
		}
	}, [filterRating, sortBy, filterStatus, selectedUserId]);

	const issueLabels = {
		"unclear-expectations": "Unclear expectations",
		"scope-changed": "Scope changed mid-job",
		"payment-delayed": "Payment delayed",
		"verbal-aggression": "Verbal aggression",
		"threatened-review": "Threatened bad review",
		"access-issues": "Access issues",
		micromanaged: "Client micromanaged",
		"safety-concerns": "Safety concerns",
		"poor-communication": "Poor communication",
		other: "Other",
	};

	const jobIssuesOptions = [
		{id: "unclear-expectations", label: "Unclear expectations"},
		{id: "scope-changed", label: "Scope changed mid-job"},
		{id: "payment-delayed", label: "Payment delayed"},
		{id: "verbal-aggression", label: "Verbal aggression"},
		{id: "threatened-review", label: "Threatened bad review"},
		{id: "access-issues", label: "Access issues (e.g. locked out, reschedules)"},
		{id: "micromanaged", label: "Client micromanaged"},
		{id: "safety-concerns", label: "Safety concerns"},
		{id: "poor-communication", label: "Poor communication"},
		{id: "other", label: "Other"},
	];

	useEffect(() => {
		// setLoading(false);
		setRatings(mockRatings);
		// setFilteredRatings(mockRatings);
		setLoadingButtons({});
		// setLoading(true);
	}, [mockRatings]);

	const handleSearch = async (query) => {
		if (!query || query.length < 3) {
			setOptions([]);
			return;
		}
		setLoadingUser(true);

		const timer = setTimeout(async () => {
			try {
				const res = await getUserList({search: query});
				setOptions(res?.data);
			} catch (error) {
				console.error("API error:", error);
			} finally {
				setLoadingUser(false);
			}
		}, 300);

		setDebounceTimer(timer);
	};

	const handleViewDetails = (rating) => {
		setSelectedRating(rating);
		setShowDetailModal(true);
	};

	const openEditModal = (rating) => {
		// if (!rating.canEdit) {
		// 	sweetAlerts("error", "This rating cannot be edited because it's older than 90 days or has been flagged.");

		// 	return;
		// }
		setEditingRating(rating);
		setEditFormData({
			id: rating.id,
			jobAddress: rating.jobAddress,
			jobRating: rating.jobRating,
			jobDate: rating.jobDate,
			jobIssues: typeof rating.jobIssues === "string" ? JSON.parse(rating.jobIssues) : rating.jobIssues,
			canEdit: rating.status === "historic" ? false : true,
		});
		setEditErrors({});
		setShowEditModal(true);
	};

	const closeEditModal = () => {
		setShowEditModal(false);
		setEditingRating(null);
		setEditFormData({});
		setEditErrors({});
		if (editingRating) {
			setLoadingButtons((prev) => ({...prev, [`edit-${editingRating.id}`]: false}));
		}
	};

	const handleEditInputChange = (field, value) => {
		setEditFormData((prev) => ({
			...prev,
			[field]: value,
		}));

		if (editErrors[field]) {
			setEditErrors((prev) => ({
				...prev,
				[field]: null,
			}));
		}
	};

	const handleEditIssueToggle = (issueId) => {
		setEditFormData((prev) => ({
			...prev,
			jobIssues: prev.jobIssues.includes(issueId) ? prev.jobIssues.filter((id) => id !== issueId) : [...prev.jobIssues, issueId],
		}));
	};

	const handleEditRatingChange = (rating) => {
		setEditFormData((prev) => ({
			...prev,
			jobRating: rating,
		}));

		if (editErrors.jobRating) {
			setEditErrors((prev) => ({
				...prev,
				jobRating: null,
			}));
		}
	};

	const validateEditForm = () => {
		const errors = {};

		if (!editFormData.jobAddress?.trim()) {
			errors.jobAddress = "Job address is required";
		}

		if (!editFormData.jobRating || editFormData.jobRating < 1 || editFormData.jobRating > 5) {
			errors.jobRating = "Please select a rating between 1 and 5 stars";
		}

		if (!editFormData.jobDate) {
			errors.jobDate = "Job date is required";
		} else {
			const jobDate = new Date(editFormData.jobDate);
			const today = new Date();
			if (jobDate > today) {
				errors.jobDate = "Job date cannot be in the future";
			}
		}

		setEditErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const getChanges = () => {
		const changes = [];
		const original = editingRating;
		const edited = editFormData;

		if (original.jobAddress !== edited.jobAddress) {
			changes.push({
				field: "Job Address",
				from: original.jobAddress,
				to: edited.jobAddress,
			});
		}

		if (original.jobRating !== edited.jobRating) {
			changes.push({
				field: "Rating",
				from: `${original.jobRating} star${original.jobRating !== 1 ? "s" : ""}`,
				to: `${edited.jobRating} star${edited.jobRating !== 1 ? "s" : ""}`,
			});
		}

		if (original.jobDate !== edited.jobDate) {
			changes.push({
				field: "Job Date",
				from: new Date(original.jobDate).toLocaleDateString(),
				to: new Date(edited.jobDate).toLocaleDateString(),
			});
		}

		const originalIssues = typeof original.jobIssues === "string" ? JSON.parse(original.jobIssues) : original.jobIssues;
		const editedIssues = edited.jobIssues;

		if (JSON.stringify(originalIssues.sort()) !== JSON.stringify(editedIssues.sort())) {
			changes.push({
				field: "Job Issues",
				from: originalIssues.length > 0 ? originalIssues.map((id) => jobIssuesOptions.find((option) => option.id === id)?.label || id).join(", ") : "None",
				to: editedIssues.length > 0 ? editedIssues.map((id) => jobIssuesOptions.find((option) => option.id === id)?.label || id).join(", ") : "None",
			});
		}

		return changes;
	};

	const handleEditSubmit = async () => {
		if (!validateEditForm()) {
			return;
		}

		const changes = getChanges();
		if (changes.length === 0) {
			sweetAlerts("warning", "No changes detected.");
			return;
		}

		// Show confirmation with changes
		const changesList = changes.map((change) => `• ${change.field}: "${change.from}" → "${change.to}"`).join("\n");

		sweetAlertQuestion(`Are you sure you want to save these changes?\n\n${changesList}\n\nNote: This edit will be tracked for quality assurance purposes.`).then(async (result) => {
			if (result === "Yes") {
				setIsSubmittingEdit(true);
				setLoadingButtons((prev) => ({...prev, [`edit-${editingRating.id}`]: true}));

				try {
					// Simulate API call
					await new Promise((resolve) => setTimeout(resolve, 2000));

					// Update the rating in the list
					const updatedRating = {
						...editingRating,
						...editFormData,
						lastEdited: new Date().toISOString(),
						editCount: (editingRating.editCount || 0) + 1,
						changes: [
							...(editingRating.changes || []),
							{
								date: new Date().toISOString(),
								changes: changes,
								editNumber: (editingRating.editCount || 0) + 1,
							},
						],
					};
					setRatings((prev) => prev.map((rating) => (rating.id === editingRating.id ? updatedRating : rating)));
					const response = await editAddressAndRating({
						...editFormData,
					});

					if (response && response.status === true) {
						sweetAlerts("success", "Rating updated successfully! Your changes have been saved and tracked.");
						await getMockRatings();
					} else {
						sweetAlerts("error", "Failed updating rating. Please try again.");
					}
					closeEditModal();
				} catch (error) {
					sweetAlerts("error", "Error updating rating. Please try again.");
				} finally {
					setIsSubmittingEdit(false);
				}
			} else {
				return;
			}
		});
	};

	const calculateJobDateStatus = (jobDate) => {
		const job = new Date(jobDate);
		const today = new Date();
		const diffInDays = Math.floor((today - job) / (1000 * 60 * 60 * 24));

		if (diffInDays <= 90) {
			return {
				type: "fresh",
				days: diffInDays,
				label: "Fresh Rating",
				description: `Job completed ${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`,
			};
		} else {
			return {
				type: "historic",
				days: diffInDays,
				label: "Previous History",
				description: `Job completed ${diffInDays} days ago (flagged as historic)`,
			};
		}
	};

	const handleEditRating = (ratingId) => {
		const rating = ratings.find((r) => r.id === ratingId);
		if (rating) {
			openEditModal(rating);
		}
	};

	const handleDeleteRating = async (ratingId) => {
		sweetAlertQuestion("Are you sure you want to delete this rating? This action cannot be undone.").then(async (result) => {
			if (result === "Yes") {
				setLoadingButtons((prev) => ({...prev, [`delete-${ratingId}`]: true}));
				try {
					const response = await deleteAddressAndRating(ratingId);

					if (response && response.status === true) {
						if (mockRatings.length === 1) {
							setPage(page - 1);
						}
						getMockRatings();
						sweetAlerts("success", "Rating deleted successfully");
					} else {
						sweetAlerts("error", "Failed deleting rating. Please try again.");
					}
				} catch (error) {
					sweetAlerts("error", "Error deleting rating. Please try again.");
				} finally {
					setLoadingButtons((prev) => ({...prev, [`delete-${ratingId}`]: false}));
				}
			}
		});
	};

	const handleFlagged = async (ratingId) => {
		setLoadingButtons((prev) => ({...prev, [`flag-${ratingId}`]: true}));
		try {
			const response = await flagAddressAndRating({
				id: ratingId,
			});

			if (response && response.status === true) {
				await getMockRatings();
			} else {
				sweetAlerts("error", "Failed flagging rating. Please try again.");
			}
		} catch (error) {
			sweetAlerts("error", "Error flagging rating. Please try again.");
		} finally {
			setLoadingButtons((prev) => ({...prev, [`flag-${ratingId}`]: false}));
		}
	};

	const renderStars = (rating) => {
		return [...Array(5)].map((_, index) => (
			<span key={index} className={`star ${index < rating ? "filled" : ""}`}>
				★
			</span>
		));
	};

	const getStatusBadge2 = (status, flagged) => {
		if (flagged) {
			return <span className="badge badge-danger">Flagged</span>;
		}

		switch (status) {
			case "fresh":
				return <span className="badge badge-success">Fresh</span>;
			case "historic":
				return <span className="badge badge-warning">Historic</span>;
			default:
				return <span className="badge badge-secondary">Unknown</span>;
		}
	};

	const getRatingColor = (rating) => {
		if (rating >= 4) return "rating-excellent";
		if (rating >= 3) return "rating-good";
		if (rating >= 2) return "rating-fair";
		return "rating-poor";
	};

	const paginate = (pageNumber) => {
		setPage(pageNumber);
	};

	const truncateWords = (text, maxChars) => {
		if (text.length <= maxChars) return text;
		return text.substring(0, maxChars) + "...";
	};

	// if (loading) {
	// 	return <CustomLoader />;
	// }

	return (
		<div className="admin-ratings-dashboard-page">
			<div className="page-header">
				<h2>Ratings</h2>
				<p>View and manage all submitted ratings ({totalLength} total)</p>
			</div>

			<div className="ratings-controls">
				<div className="filter-section">
					<select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
						<option value="all">All Status</option>
						<option value="fresh">Fresh Ratings</option>
						<option value="historic">Historic Ratings</option>
						<option value="flagged">Flagged Ratings</option>
					</select>

					<select className="filter-select" value={filterRating} onChange={(e) => setFilterRating(e.target.value)}>
						<option value="all">All Ratings</option>
						<option value="5">5 Stars</option>
						<option value="4">4 Stars</option>
						<option value="3">3 Stars</option>
						<option value="2">2 Stars</option>
						<option value="1">1 Star</option>
					</select>

					<select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
						<option value="date-desc">Newest First</option>
						<option value="date-asc">Oldest First</option>
						<option value="rating-desc">Highest Rating</option>
						<option value="rating-asc">Lowest Rating</option>
					</select>
					<Autocomplete
						open={open}
						onOpen={() => setOpen(true)}
						onClose={() => setOpen(false)}
						options={options}
						getOptionLabel={(option) => option?.first_name + " " + option?.last_name || ""}
						loading={loadingUser}
						inputValue={inputValue}
						onInputChange={(e, value, reason) => {
							setInputValue(value);
							handleSearch(value);

							// Clear selection when user types or backspaces
							if (reason === "input") {
								const selectedUser = selectedUserId ? options.find((opt) => opt.user_id === selectedUserId) : null;
								const selectedUserName = selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : "";

								// If the current input doesn't match the selected user's name, clear selection
								if (value !== selectedUserName) {
									setSelectedUserId(null);
								}
							}
						}}
						onChange={(e, value) => {
							if (value) {
								setSelectedUserId(value.user_id);
								setInputValue(value.first_name + " " + value.last_name);
							} else {
								setSelectedUserId(null);
								setInputValue("");
							}
						}}
						value={selectedUserId ? options.find((opt) => opt.user_id === selectedUserId) || null : null}
						clearIcon={<i className="fa-2xs fas fa-close" />}
						popupIcon={null}
						autoComplete
						includeInputInList
						filterSelectedOptions
						renderInput={(params) => (
							<TextField
								{...params}
								placeholder="User Name"
								sx={{
									"& .MuiOutlinedInput-root": {
										borderRadius: "10px",
										backgroundColor: "#fff",
										"& .MuiOutlinedInput-input": {
											padding: "10px 2px",
											height: 3,
										},
										"& fieldset": {
											borderColor: "#e9ecef",
											borderWidth: "2px",
										},
										"&:hover fieldset": {
											borderColor: "#e9ecef",
											borderWidth: "2px",
										},
										"&.Mui-focused fieldset": {
											borderColor: "#28a745",
											borderWidth: "2px",
											boxShadow: "0 0 0 .2rem #22c55e40",
										},
									},
								}}
								InputProps={{
									...params.InputProps,
									endAdornment: <>{params.InputProps.endAdornment}</>,
								}}
							/>
						)}
						sx={{width: 250}}
					/>
				</div>
			</div>

			{loading ? (
				<div className="admin-ratings-container">
					{mockRatings.length === 0 ? (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Address</th>
											<th className="align-middle address-cell">User Name</th>
											<th className="align-middle address-cell">Rating</th>
											<th className="align-middle address-cell">Posted date</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Submitted</th>
											<th className="actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr className="rating-row">
											<td className="address-cell text-start" colSpan={7}>
												No ratings found
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					) : (
						<div className="ratings-table-container">
							<div className="table-responsive sticky-table">
								<table className="ratings-table">
									<thead>
										<tr>
											<th className="align-middle address-cell">Address</th>
											<th className="align-middle address-cell">User Name</th>
											<th className="align-middle address-cell">Rating</th>
											<th className="align-middle address-cell">Posted date</th>
											<th className="align-middle address-cell">Status</th>
											<th className="align-middle address-cell">Submitted</th>
											<th className="actions-cell text-end">Actions</th>
										</tr>
									</thead>
									<tbody>
										{mockRatings.map((rating) => (
											<tr key={rating.id} className={`rating-row ${rating.flagged ? "flagged" : ""}`} data-id={rating.id}>
												<td className="align-middle address-cell cursor-pointer" title={rating.jobAddress} onClick={() => handleViewDetails(rating)}>
													{truncateWords(rating.jobAddress, 50)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(rating)} title={rating.userName}>
													<CustomUserName user={rating?.item?.user} />
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(rating)}>
													<CustomRatingStar star={rating?.jobRating} />
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(rating)}>
													{fDate(rating.jobDate)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(rating)}>
													{getStatusBadge(rating?.item?.is_historic, rating.flagged)}
												</td>
												<td className="align-middle address-cell cursor-pointer" onClick={() => handleViewDetails(rating)}>
													{fDate(rating.submittedDate)}
												</td>
												<td className="align-middle actions-cell">
													<div className="table-actions mb-0 justify-content-end flex-row">
														<button className="btn-icon" onClick={() => handleFlagged(rating.id)} title="Flag Rating" disabled={loadingButtons[`flag-${rating.id}`]}>
															{loadingButtons[`flag-${rating.id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
																	<path
																		d="M10.29 3.86L1.82 18C1.64 18.37 1.54 18.78 1.54 19.2C1.54 19.62 1.64 20.03 1.82 20.4C2 20.77 2.26 21.09 2.59 21.33C2.92 21.57 3.31 21.73 3.72 21.8C4.13 21.87 4.55 21.85 4.95 21.75C5.35 21.65 5.72 21.47 6.04 21.22L12 17.27L17.96 21.22C18.28 21.47 18.65 21.65 19.05 21.75C19.45 21.85 19.87 21.87 20.28 21.8C20.69 21.73 21.08 21.57 21.41 21.33C21.74 21.09 22 20.77 22.18 20.4C22.36 20.03 22.46 19.62 22.46 19.2C22.46 18.78 22.36 18.37 22.18 18L13.71 3.86C13.53 3.49 13.27 3.17 12.94 2.93C12.61 2.69 12.22 2.53 11.81 2.46C11.4 2.39 10.98 2.41 10.58 2.51C10.18 2.61 9.81 2.79 9.49 3.04C9.17 3.29 8.91 3.61 8.73 3.98C8.55 4.35 8.45 4.76 8.45 5.18C8.45 5.6 8.55 6.01 8.73 6.38L10.29 3.86Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																	<path d="M12 9V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																</svg>
															)}
														</button>
														<button className="btn-icon" onClick={() => handleViewDetails(rating)} title="View Details">
															<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																<path d="M1 12S5 4 12 4S23 12 23 12S19 20 12 20S1 12 1 12Z" stroke="currentColor" strokeWidth="2" />
																<circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
															</svg>
														</button>
														<button className="btn-icon btn-edit" onClick={() => handleEditRating(rating.id)} title="Edit Rating" disabled={loadingButtons[`edit-${rating.id}`]}>
															{loadingButtons[`edit-${rating.id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path
																		d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																	<path
																		d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>

														<button className="btn-icon btn-delete" onClick={() => handleDeleteRating(rating.id)} title="Delete Rating" disabled={loadingButtons[`delete-${rating.id}`]}>
															{loadingButtons[`delete-${rating.id}`] ? (
																<span className="spinner-small"></span>
															) : (
																<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
																	<path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
																	<path
																		d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
																		stroke="currentColor"
																		strokeWidth="2"
																		strokeLinecap="round"
																		strokeLinejoin="round"
																	/>
																</svg>
															)}
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Pagination */}
							<>
								<div className="pagination justify-content-between mt-0 pt-0 pe-3 px-3">
									<div className="pagination-info d-flex justify-content-between align-items-center gap-3">
										<span className="pagination-info-text">
											Showing {page * pageSize - pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
										</span>
										<div>
											<select className="filter-select" value={pageSize} onChange={(e) => setPageSize(e.target.value)}>
												<option value="10">10</option>
												<option value="20">20</option>
												<option value="25">25</option>
												<option value="50">50</option>
												<option value="100">100</option>
											</select>
										</div>
									</div>
									<div className="d-flex justify-content-end pb-3 pt-3 pe-2">
										<button className="pagination-btn" onClick={() => paginate(page - 1)} disabled={page === 1}>
											Previous
										</button>

										<div className="pagination-numbers">
											{[...Array(totalPages)].map((_, index) => {
												const ipage = index + 1;
												if (ipage === 1 || ipage === totalPages || (ipage >= page - 1 && ipage <= page + 1)) {
													return (
														<button key={ipage} className={`pagination-number ${page === ipage ? "active" : ""}`} onClick={() => paginate(ipage)}>
															{ipage}
														</button>
													);
												} else if (ipage === page - 2 || ipage === page + 2) {
													return (
														<span key={ipage} className="pagination-ellipsis">
															...
														</span>
													);
												}
												return null;
											})}
										</div>

										<button className="pagination-btn" onClick={() => paginate(page + 1)} disabled={page === totalPages}>
											Next
										</button>
									</div>
								</div>
							</>
						</div>
					)}
				</div>
			) : (
				<div className="admin-ratings-container">
					{/* <CustomLoader /> */}
					<div className="ratings-table-container">
						<div className="table-responsive sticky-table">
							<table className="ratings-table">
								<thead>
									<tr>
										<th className="align-middle address-cell">Address</th>
										<th className="align-middle address-cell">User Name</th>
										<th className="align-middle address-cell">Rating</th>
										<th className="align-middle address-cell">Posted date</th>
										<th className="align-middle address-cell">Status</th>
										{/* <th className="align-middle address-cell">Submitted</th> */}
										<th className="actions-cell text-end">Actions</th>
									</tr>
								</thead>
								<tbody>
									<tr className="rating-row">
										<td className="address-cell text-center" colSpan={6}>
											<CustomLoader />
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}

			{/* Detail Modal */}
			{showDetailModal && selectedRating && (
				<div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h3>Rating Details</h3>
							<button className="modal-close" onClick={() => setShowDetailModal(false)}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="detail-section">
								<h4>Job Information</h4>
								<div className="detail-grid">
									<div className="detail-item">
										<span className="detail-label">Address:</span>
										<span className="detail-value">{selectedRating.jobAddress}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Posted date:</span>
										<span className="detail-value">
											{new Date(selectedRating.jobDate).toLocaleDateString()}
											<span className="days-ago"> ({selectedRating.daysAgo} days ago)</span>
										</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Rating:</span>
										<div className="detail-rating">
											<div className={`rating-stars ${getRatingColor(selectedRating.jobRating)}`}>
												{renderStars(selectedRating.jobRating)}
												<span className="rating-number">({selectedRating.jobRating}/5)</span>
											</div>
										</div>
									</div>
									<div className="detail-item">
										<span className="detail-label">Status:</span>
										<span className="detail-value">{getStatusBadge2(selectedRating.status, selectedRating.flagged)}</span>
									</div>
								</div>
							</div>

							{Array.isArray(selectedRating?.jobIssues) && selectedRating.jobIssues.length > 0 && (
								<div className="detail-section">
									<h4>Issues Reported</h4>
									<div className="issues-list-modal">
										{selectedRating.jobIssues.map((issue) => (
											<span key={issue} className="issue-tag-modal">
												{issueLabels[issue]}
											</span>
										))}
									</div>
								</div>
							)}

							{/* {selectedRating.jobDescription && (
								<div className="detail-section">
								<h4>Job Description</h4>
								<p className="job-description-full">{selectedRating.jobDescription}</p>
								</div>
							)} */}

							<div className="detail-section">
								<h4>Submission Information</h4>
								<div className="detail-grid">
									<div className="detail-item">
										<span className="detail-label">Submitted:</span>
										<span className="detail-value">{new Date(selectedRating.submittedDate).toLocaleDateString()}</span>
									</div>
									<div className="detail-item">
										<span className="detail-label">Can Edit:</span>
										<span className="detail-value">{selectedRating.canEdit ? "Yes" : "No"}</span>
									</div>
								</div>
							</div>
						</div>

						<div className="modal-footer">
							{selectedRating.canEdit && (
								<>
									<button
										className="btn btn-primary"
										onClick={() => {
											setShowDetailModal(false);
											handleEditRating(selectedRating.id);
										}}>
										Edit Rating
									</button>
									<button
										className="btn btn-danger"
										onClick={() => {
											setShowDetailModal(false);
											handleDeleteRating(selectedRating.id);
										}}>
										Delete Rating
									</button>
								</>
							)}
							<button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
								Close
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Edit Modal */}
			{showEditModal && editingRating && (
				<div className="modal-overlay" onClick={closeEditModal}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: "800px"}}>
						<div className="modal-header">
							<h3>Edit Rating</h3>
							<button className="modal-close" onClick={closeEditModal}>
								×
							</button>
						</div>

						<div className="modal-body">
							<div className="form-container">
								{/* Job Address */}
								<div className="form-group">
									<label className="form-label">Job Address *</label>
									<input
										type="text"
										className={`form-input ${editErrors.jobAddress ? "error" : ""}`}
										value={editFormData.jobAddress || ""}
										onChange={(e) => handleEditInputChange("jobAddress", e.target.value)}
										placeholder="Enter job site address"
										readOnly
									/>
									{editErrors.jobAddress && <div className="error-message">{editErrors.jobAddress}</div>}
								</div>

								{/* Job Issues */}
								<div className="form-group">
									<label className="form-label">Job Issues (Select all that apply)</label>
									<div className="job-issues-grid">
										{jobIssuesOptions.map((issue) => (
											<div key={issue.id} className="issue-checkbox" onClick={() => handleEditIssueToggle(issue.id)} style={{cursor: "pointer"}}>
												<input type="checkbox" id={`edit-issue-${issue.id}`} checked={editFormData.jobIssues?.includes(issue.id) || false} onChange={() => handleEditIssueToggle(issue.id)} />
												<div className="checkbox-custom"></div>
												<label htmlFor={`edit-issue-${issue.id}`} className="checkbox-label">
													{issue.label}
												</label>
											</div>
										))}
									</div>
								</div>

								{/* Job Rating */}
								<div className="form-group">
									<label className="form-label">Job Rating *</label>
									<div className="rating-container">
										<div className="star-rating-large">
											{[1, 2, 3, 4, 5].map((star) => (
												<button key={star} type="button" className={`star-btn-large ${star <= (editFormData.jobRating || 0) ? "filled" : ""}`} onClick={() => handleEditRatingChange(star)}>
													★
												</button>
											))}
										</div>
										<div className="rating-text">
											{editFormData.jobRating ? (
												<span className="rating-description">
													{editFormData.jobRating === 5 ? "Excellent" : editFormData.jobRating === 4 ? "Good" : editFormData.jobRating === 3 ? "Fair" : editFormData.jobRating === 2 ? "Poor" : "Very Poor"}
												</span>
											) : (
												<span className="rating-description">Select a rating</span>
											)}
										</div>
									</div>
									{editErrors.jobRating && <div className="error-message">{editErrors.jobRating}</div>}
								</div>

								{/* Posted date */}
								<div className="form-group">
									<label className="form-label">Posted date *</label>
									<input
										type="date"
										onClick={(e) => {
											e.target.showPicker && e.target.showPicker();
										}}
										className={`form-input ${editErrors.jobDate ? "error" : ""}`}
										value={editFormData.jobDate || ""}
										onChange={(e) => handleEditInputChange("jobDate", e.target.value)}
									/>
									{editFormData.jobDate && (
										<div className={`date-status ${calculateJobDateStatus(editFormData.jobDate).type}`}>
											<span className="status-icon">{calculateJobDateStatus(editFormData.jobDate).type === "fresh" ? "🟢" : "🟡"}</span>
											<span className={`status-${calculateJobDateStatus(editFormData.jobDate).type}`}>{calculateJobDateStatus(editFormData.jobDate).label}</span>
											<small>{calculateJobDateStatus(editFormData.jobDate).description}</small>
										</div>
									)}
									{editErrors.jobDate && <div className="error-message">{editErrors.jobDate}</div>}
								</div>

								{/* Change Summary */}
								{editingRating && (
									<div className="form-group">
										<label className="form-label">Changes Summary</label>
										<div className="changes-preview">
											{getChanges().length > 0 ? (
												<div className="changes-list">
													{getChanges().map((change, index) => (
														<div key={index} className="change-item">
															<strong>{change.field}:</strong>
															<span className="change-from">"{change.from}"</span>
															<span className="change-arrow">→</span>
															<span className="change-to">"{change.to}"</span>
														</div>
													))}
												</div>
											) : (
												<p className="no-changes">No changes detected</p>
											)}
										</div>
									</div>
								)}

								{/* Edit History */}
								{editingRating?.changes && editingRating.changes.length > 0 && (
									<div className="form-group">
										<label className="form-label">Edit History</label>
										<div className="edit-history">
											{editingRating.changes.map((edit, index) => (
												<div key={index} className="edit-entry">
													<div className="edit-header">
														<strong>Edit #{edit.editNumber}</strong>
														<span className="edit-date">
															{new Date(edit.date).toLocaleDateString()} at {new Date(edit.date).toLocaleTimeString()}
														</span>
													</div>
													<div className="edit-changes">
														{edit.changes.map((change, changeIndex) => (
															<div key={changeIndex} className="edit-change">
																{change.field}: "{change.from}" → "{change.to}"
															</div>
														))}
													</div>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</div>

						<div className="modal-footer">
							<button className="btn btn-primary" onClick={handleEditSubmit} disabled={isSubmittingEdit || getChanges().length === 0}>
								{isSubmittingEdit ? (
									<>
										<span className="spinner-small"></span>
										Saving Changes...
									</>
								) : (
									"Save Changes"
								)}
							</button>
							<button className="btn btn-secondary" onClick={closeEditModal} disabled={isSubmittingEdit}>
								Cancel
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default MyRatings;
